import { GetTeamsRequest, Team } from "@/Stub/V1";
import { Column, ColumnBodyOptions } from "primereact/column";
import { DataTable, DataTablePassThroughOptions, DataTableStateEvent } from "primereact/datatable";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ReactNode, useMemo } from "react";
import {  url } from "@/Helpers/general";
import { Button } from "primereact/button";
import { useUpdateSearchParams } from "@/Hooks/useUpdateSearchParams";
import { useGetTeams } from "@/Service/Api/V1/ApiHooks/Teams/useGetTeams";
import useGetTeamsFiltersQuery, { TeamFilterType } from "@/Hooks/useGetTeamsFiltersQuery";


type TableColumnDefinition = {
    label: string
    sortable?: boolean
    field?: string
    sortField?: string
    headerTooltip?: string
    body?: ReactNode | ((data: Team, options: ColumnBodyOptions) => ReactNode)
};

const dataTablePtOptions: DataTablePassThroughOptions = {
    root: {
        className: 'datatable-base'
    }
};

const StyledWrap = styled.main`
    .content-header {
        padding: 1rem;
        border-bottom: var(--gray-300) 1px solid;

        &__title {
            margin: 0.25rem 0;
            font-weight: 600;
            font-size: 1.5rem;
        }

        &__description {
            font-size: 0.9rem;
            color: var(--gray-500);
        }
    }

    .content-container {
        margin-top: 2rem;
        padding: 0 1rem;
    }

    .datatable-container {
        margin-top: 2rem;
        font-size: 0.8rem;
    }

    .datatable-base {
        font-size: 0.8rem;
        font-weight: 500;
    }

    .datatable-cell {
        &__clean-text {
            margin: 0;
        }
    }

    .angles-sort {
        margin-left: 0.3rem;

        &__icon {
            color: var(--primary-200);
            font-size: 0.8rem;
            font-weight: 600;

            &.active {
                color: var(--primary-500);
            }
        }
    }
`;


export type TeamsDatableProps = {
    requestParams?: GetTeamsRequest
};


const TeamsDatable = ({ requestParams }: TeamsDatableProps) => {
    const navigate = useNavigate();
    const {
        pageQuery,
        pageSizeQuery
    } = useGetTeamsFiltersQuery();
    const updateUsersFilterHandler = useUpdateSearchParams();


    const teamsRequest = useMemo<GetTeamsRequest>(() => {
        return {
            ...requestParams,
            page: pageQuery ? parseInt(pageQuery) : 1,
            per_page: pageSizeQuery ? parseInt(pageSizeQuery) : 10,
        };
    }, [requestParams, pageQuery, pageSizeQuery]);

    const {
        data: teamsData,
        isLoading: customersLoading
    } = useGetTeams({
        requestParams: teamsRequest
    });



    const navigateToTeam = (team: Team) => {
        navigate(url('team', { teamId: team.id }));
    };

    const actionsBodyTemplate = (team: Team) => {
        return (
            <div className="flex gap-2">
                <Button
                    onClick={() => navigateToTeam(team)}
                    label='View'
                />
            </div>
        );
    };



    const tableColumns: TableColumnDefinition[] = [
        {
            label: 'Id',
            field: 'id',
            body: (team) => {
                return <>
                    {team.id ?? 'N/A'}
                </>;

            }
        },
        {
            label: 'Client',
            field: 'client.name',
            body: (team) => {
                return <>
                    {team.client?.name ?? 'N/A'}
                </>;

            }
        },
        {
            label: 'Name',
            field: 'name',
            body: (team) => {
                return <>
                    {team.name ?? 'N/A'}
                </>;

            }
        },
        {
            label: 'Actions',
            body: actionsBodyTemplate
        }
    ];


    const onPagination = (e: DataTableStateEvent) => {
        updateUsersFilterHandler(TeamFilterType.Page, ((e.first / e.rows) + 1).toString());
        updateUsersFilterHandler(TeamFilterType.PageSize, e.rows.toString());
    };

    const first = pageSizeQuery && pageQuery ? Number(pageSizeQuery) * Number(pageQuery) - 10 : 0;
    const rows = pageSizeQuery ? Number(pageSizeQuery) : 10;

    return (
        <StyledWrap>
            <div className="content-container">
                <DataTable
                    lazy
                    loading={customersLoading}
                    emptyMessage="No customers found."
                    value={teamsData?.data}
                    totalRecords={teamsData?.meta.total}
                    paginator
                    rows={rows}
                    rowsPerPageOptions={[10, 20, 50]}
                    first={first}
                    pt={dataTablePtOptions}
                    onPage={e => {
                        onPagination(e);
                    }}
                >
                    {tableColumns.map((column, index) =>
                        <Column
                            key={`teams-column-${index}`}
                            field={column.field}
                            body={column.body}
                            header={column.label}
                            sortable={column.sortable}
                            sortField={column.sortField}
                            headerTooltip={column.headerTooltip}
                        />
                    )}
                </DataTable>
            </div>
        </StyledWrap>
    );
};

export default TeamsDatable;
